/**
 * AMS360 Integration Service (EMS REST API)
 *
 * Frontend service for AMS360 customer search, document retrieval, and report upload
 * Now uses EMS REST API with OAuth2 authentication instead of SOAP WSAPI
 */

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || '/api';

// ============================================================================
// TYPES
// ============================================================================

export interface AMS360Customer {
  customerId: string; // GUID from AMS360
  customerName: string;
  customerNumber?: string;
  address?: string;
  city?: string;
  state?: string;
  postalCode?: string;
  phone?: string;
  email?: string;
  isCommercial?: boolean;
  entityType?: string;
}

export interface AMS360Document {
  documentId: string;
  fileName: string;
  description?: string;
  documentType?: string;
  dateCreated?: string;
  fileSize?: number;
  activityId?: string;
}

export interface AMS360Settings {
  enabled: boolean;
  appKey: string | null; // App Key (from AMS360/Vertafore)
  hasAppKey: boolean; // Whether app key is configured
  hasAccessKey: boolean; // Whether access key is configured
  email: string | null; // Contact email
  agencyId: string | null; // Agency/Instance ID
  environment: 'production' | 'sandbox';
  apiBaseUrl: string; // Current API base URL
}

export interface UploadReportParams {
  customerId: string;
  reportTitle: string;
  pdfBase64: string;
  description?: string;
}

export interface UploadReportResult {
  success: boolean;
  message: string;
  activityId?: string;
  documentId?: string;
}

export interface CreateActivityParams {
  customerId: string;
  description: string;
  actionCode?: string; // 'NOTE', 'CALL', 'EMAIL', 'TASK', 'RPT'
  suspenseDate?: string; // ISO date string for follow-up (can include time: "2024-01-15T14:30")
  suspenseAction?: string; // 'FOLLOWUP', 'CALL', 'REVIEW'
  assignedTo?: string; // Employee code to assign the activity to
}

export interface CreateActivityResult {
  success: boolean;
  message: string;
  activityId?: string;
  suspenseId?: string;
}

export interface AttachToActivityParams {
  activityId: string;
  fileName: string;
  fileBase64: string;
  documentType?: string; // 'DOC', 'PDF', 'IMG'
  description?: string;
}

export interface AttachToActivityResult {
  success: boolean;
  message: string;
  documentId?: string;
}

export interface CreateActivityWithAttachmentParams {
  customerId: string;
  description: string;
  fileName: string;
  fileBase64: string;
  actionCode?: string;
  documentType?: string;
  suspenseDate?: string;
  suspenseAction?: string;
  assignedTo?: string; // Employee code to assign the activity to
}

export interface CreateSuspenseParams {
  customerId: string;
  suspenseDate: string;
  description: string;
  actionCode?: string;
  assignedTo?: string; // Employee code to assign the suspense to
}

export interface AMS360Employee {
  employeeCode: string;
  firstName: string;
  lastName: string;
  fullName: string;
  email?: string;
  isActive: boolean;
}

export interface TestConnectionResult {
  success: boolean;
  message: string;
}

// ============================================================================
// SERVICE
// ============================================================================

class AMS360Service {
  private getAuthHeaders(): HeadersInit {
    return {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${localStorage.getItem('riskninja-token')}`
    };
  }

  /**
   * Search for customers in AMS360
   */
  async searchCustomers(query: string, maxRecords: number = 20): Promise<AMS360Customer[]> {
    try {
      const response = await fetch(`${API_BASE_URL}/api/ams360/search-customers`, {
        method: 'POST',
        headers: this.getAuthHeaders(),
        body: JSON.stringify({ query, maxRecords })
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.error || 'Failed to search customers');
      }

      const data = await response.json();
      return data.customers || [];
    } catch (error: any) {
      console.error('AMS360 search customers error:', error);
      throw error;
    }
  }

  /**
   * Get documents for a customer from AMS360
   * @param customerId - The LOCAL customer ID (not AMS360 GUID)
   */
  async getDocuments(customerId: string): Promise<AMS360Document[]> {
    try {
      const response = await fetch(`${API_BASE_URL}/api/ams360/documents/${customerId}`, {
        headers: this.getAuthHeaders()
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.error || 'Failed to get documents');
      }

      const data = await response.json();
      return data.documents || [];
    } catch (error: any) {
      console.error('AMS360 get documents error:', error);
      throw error;
    }
  }

  /**
   * Get documents using AMS360 customer ID directly
   * @param ams360CustomerId - The AMS360 customer GUID
   */
  async getDocumentsByAmsId(ams360CustomerId: string): Promise<AMS360Document[]> {
    try {
      const response = await fetch(`${API_BASE_URL}/api/ams360/documents-by-ams-id/${ams360CustomerId}`, {
        headers: this.getAuthHeaders()
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.error || 'Failed to get documents');
      }

      const data = await response.json();
      return data.documents || [];
    } catch (error: any) {
      console.error('AMS360 get documents by AMS ID error:', error);
      throw error;
    }
  }

  /**
   * Get document content as base64 for workflow processing
   * @param attachmentId - The AMS360 attachment GUID
   */
  async getDocumentContent(attachmentId: string): Promise<{
    success: boolean;
    attachmentId: string;
    fileName: string;
    fileType: string;
    fileSize: number;
    content: string; // base64
  }> {
    try {
      const response = await fetch(`${API_BASE_URL}/api/ams360/document-content/${attachmentId}`, {
        headers: this.getAuthHeaders()
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.error || 'Failed to get document content');
      }

      return await response.json();
    } catch (error: any) {
      console.error('AMS360 get document content error:', error);
      throw error;
    }
  }

  /**
   * Download document as blob (for file downloads)
   * @param attachmentId - The AMS360 attachment GUID
   */
  async downloadDocument(attachmentId: string): Promise<Blob> {
    try {
      const response = await fetch(`${API_BASE_URL}/api/ams360/download/${attachmentId}`, {
        headers: this.getAuthHeaders()
      });

      if (!response.ok) {
        throw new Error('Failed to download document');
      }

      return await response.blob();
    } catch (error: any) {
      console.error('AMS360 download document error:', error);
      throw error;
    }
  }

  /**
   * Upload a report to AMS360
   */
  async uploadReport(params: UploadReportParams): Promise<UploadReportResult> {
    try {
      const response = await fetch(`${API_BASE_URL}/api/ams360/upload-report`, {
        method: 'POST',
        headers: this.getAuthHeaders(),
        body: JSON.stringify(params)
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.error || 'Failed to upload report');
      }

      return await response.json();
    } catch (error: any) {
      console.error('AMS360 upload report error:', error);
      throw error;
    }
  }

  /**
   * Test AMS360 connection
   */
  async testConnection(): Promise<TestConnectionResult> {
    try {
      const response = await fetch(`${API_BASE_URL}/api/ams360/test-connection`, {
        headers: this.getAuthHeaders()
      });

      return await response.json();
    } catch (error: any) {
      console.error('AMS360 test connection error:', error);
      return { success: false, message: error.message || 'Connection test failed' };
    }
  }

  /**
   * Get employees from AMS360 for user assignment dropdowns
   */
  async getEmployees(activeOnly: boolean = true): Promise<AMS360Employee[]> {
    try {
      const response = await fetch(`${API_BASE_URL}/api/ams360/employees?activeOnly=${activeOnly}`, {
        headers: this.getAuthHeaders()
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.error || 'Failed to get employees');
      }

      const data = await response.json();
      return data.employees || [];
    } catch (error: any) {
      console.error('AMS360 get employees error:', error);
      return []; // Return empty array on error - employees may not be accessible
    }
  }

  /**
   * Import a customer from AMS360 to local database
   */
  async importCustomer(ams360Customer: AMS360Customer, type: 'customer' | 'prospect' | 'suspect' = 'customer'): Promise<{ success: boolean; customerId?: string; error?: string }> {
    try {
      const response = await fetch(`${API_BASE_URL}/api/ams360/import-customer`, {
        method: 'POST',
        headers: this.getAuthHeaders(),
        body: JSON.stringify({ ams360Customer, type })
      });

      if (!response.ok) {
        const error = await response.json();
        // Check if it's a "already exists" error
        if (response.status === 409) {
          return { success: true, customerId: error.customerId };
        }
        throw new Error(error.error || 'Failed to import customer');
      }

      const data = await response.json();
      return { success: true, customerId: data.customer?.id };
    } catch (error: any) {
      console.error('AMS360 import customer error:', error);
      return { success: false, error: error.message };
    }
  }

  // ============================================================================
  // ACTIVITY & SUSPENSE MANAGEMENT
  // ============================================================================

  /**
   * Create an activity in AMS360 with optional suspense (follow-up)
   */
  async createActivity(params: CreateActivityParams): Promise<CreateActivityResult> {
    try {
      const response = await fetch(`${API_BASE_URL}/api/ams360/create-activity`, {
        method: 'POST',
        headers: this.getAuthHeaders(),
        body: JSON.stringify(params)
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.error || 'Failed to create activity');
      }

      return await response.json();
    } catch (error: any) {
      console.error('AMS360 create activity error:', error);
      throw error;
    }
  }

  /**
   * Attach a file to an existing AMS360 activity
   */
  async attachToActivity(params: AttachToActivityParams): Promise<AttachToActivityResult> {
    try {
      const response = await fetch(`${API_BASE_URL}/api/ams360/attach-to-activity`, {
        method: 'POST',
        headers: this.getAuthHeaders(),
        body: JSON.stringify(params)
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.error || 'Failed to attach file');
      }

      return await response.json();
    } catch (error: any) {
      console.error('AMS360 attach to activity error:', error);
      throw error;
    }
  }

  /**
   * Create an activity with attachment in one call
   * Useful for attaching reports to AMS360
   */
  async createActivityWithAttachment(params: CreateActivityWithAttachmentParams): Promise<CreateActivityResult & { documentId?: string }> {
    try {
      const response = await fetch(`${API_BASE_URL}/api/ams360/create-activity-with-attachment`, {
        method: 'POST',
        headers: this.getAuthHeaders(),
        body: JSON.stringify(params)
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.error || 'Failed to create activity with attachment');
      }

      return await response.json();
    } catch (error: any) {
      console.error('AMS360 create activity with attachment error:', error);
      throw error;
    }
  }

  /**
   * Create a standalone suspense (follow-up task) for a customer
   */
  async createSuspense(params: CreateSuspenseParams): Promise<{ success: boolean; message: string; suspenseId?: string }> {
    try {
      const response = await fetch(`${API_BASE_URL}/api/ams360/create-suspense`, {
        method: 'POST',
        headers: this.getAuthHeaders(),
        body: JSON.stringify(params)
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.error || 'Failed to create suspense');
      }

      return await response.json();
    } catch (error: any) {
      console.error('AMS360 create suspense error:', error);
      throw error;
    }
  }

  // ============================================================================
  // ADMIN SETTINGS
  // ============================================================================

  /**
   * Get AMS360 settings for the company (admin only)
   */
  async getSettings(): Promise<AMS360Settings> {
    try {
      const response = await fetch(`${API_BASE_URL}/api/admin/company/ams360`, {
        headers: this.getAuthHeaders()
      });

      if (!response.ok) {
        throw new Error('Failed to get AMS360 settings');
      }

      const data = await response.json();
      // Map backend field names to frontend expected names
      return {
        enabled: data.enabled,
        appKey: data.appKey || null,
        hasAppKey: data.hasAppKey,
        hasAccessKey: data.hasAccessKey,
        email: data.email,
        agencyId: data.agencyNo || data.agencyId || null,
        environment: data.environment,
        apiBaseUrl: data.apiBaseUrl
      };
    } catch (error: any) {
      console.error('AMS360 get settings error:', error);
      throw error;
    }
  }

  /**
   * Update AMS360 credentials (admin only)
   * Uses EMS username + access key authentication
   */
  async updateCredentials(params: {
    appKey: string;
    accessKey: string;
    email?: string;
    agencyId: string;
    environment?: 'production' | 'sandbox';
  }): Promise<{ success: boolean; message: string }> {
    try {
      // Map frontend field names to backend expected names
      const backendParams = {
        appKey: params.appKey,
        appAccessToAgencyKey: params.accessKey,
        agencyNo: params.agencyId,
        email: params.email,
        environment: params.environment
      };
      const response = await fetch(`${API_BASE_URL}/api/admin/company/ams360`, {
        method: 'PUT',
        headers: this.getAuthHeaders(),
        body: JSON.stringify(backendParams)
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.error || 'Failed to update credentials');
      }

      return await response.json();
    } catch (error: any) {
      console.error('AMS360 update credentials error:', error);
      throw error;
    }
  }

  /**
   * Test AMS360 connection (admin)
   */
  async testAdminConnection(): Promise<TestConnectionResult> {
    try {
      const response = await fetch(`${API_BASE_URL}/api/admin/company/ams360/test`, {
        method: 'POST',
        headers: this.getAuthHeaders()
      });

      return await response.json();
    } catch (error: any) {
      console.error('AMS360 test admin connection error:', error);
      return { success: false, message: error.message || 'Connection test failed' };
    }
  }

  // ============================================================================
  // PDF GENERATION
  // ============================================================================

  /**
   * Generate a branded PDF using server-side rendering
   * This produces the same professional PDF as the "Export PDF" button
   * with company branding (logo, header/footer text)
   *
   * @param content - Report content (markdown or structured JSON)
   * @param title - Report title for the PDF
   * @param workflowType - Optional workflow type for metadata
   * @returns Base64-encoded PDF (no data URI prefix)
   */
  async generateBrandedPdf(content: string, title: string, workflowType?: string): Promise<string> {
    try {
      const response = await fetch(`${API_BASE_URL}/api/workflows/reports/generate-branded-pdf`, {
        method: 'POST',
        headers: this.getAuthHeaders(),
        body: JSON.stringify({ content, title, workflowType })
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.error || 'Failed to generate branded PDF');
      }

      const data = await response.json();
      if (!data.success || !data.pdfBase64) {
        throw new Error(data.error || 'PDF generation failed');
      }

      return data.pdfBase64;
    } catch (error: any) {
      console.error('AMS360 generate branded PDF error:', error);
      throw error;
    }
  }
}

export const ams360Service = new AMS360Service();
export default ams360Service;
